import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet as WalletIcon, Plus, Send, ArrowDownToLine, History, CreditCard, Zap, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useApiQuery, useQueryClient } from "../lib/useApiQuery";
import { Loader } from "../components/Loader";
import { PageHeader } from "../components/PageHeader";
import { fileToCompressedDataUrl } from "../lib/image";

export default function Wallet() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("tlync");
  const [method, setMethod] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: balance = null, isLoading } = useApiQuery(["wallet", "balance"], "/wallet/balance", { staleTime: 10000 });
  const { data: methods = [] } = useApiQuery(["wallet", "payment-methods"], "/wallet/payment-methods");

  const onReceipt = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { setReceipt(await fileToCompressedDataUrl(f, 1080, 0.5)); } catch {}
  };

  const topup = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("أدخل مبلغاً صحيحاً");
    setBusy(true);
    try {
      if (mode === "tlync") {
        const res = await api("/payments/tlync/initiate", { method: "POST", body: JSON.stringify({ purpose: "wallet_topup", amount: amt, phone: user?.phone || "0910000000", email: user?.email || "" }) });
        if (res.redirect_url) window.location.href = res.redirect_url;
      } else {
        if (!method) return toast.error("اختر طريقة الدفع");
        if (!receipt) return toast.error("ارفع إيصال التحويل");
        await api("/wallet/topup", { method: "POST", body: JSON.stringify({ amount: amt, payment_method_id: method.method_id, receipt_base64: receipt }) });
        toast.success("تم إرسال طلب الشحن — بانتظار موافقة الإدارة");
        setAmount(""); setReceipt(null); refresh(); qc.invalidateQueries({ queryKey: ["wallet"] });
      }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (isLoading || balance === null) return <Loader />;
  const bal = Number(balance.balance ?? balance.wallet_balance ?? user?.wallet_balance ?? 0);

  return (
    <div className="animate-fade-in-up max-w-lg mx-auto">
      <PageHeader title="المحفظة" />
      <div className="rounded-3xl p-6 bg-gradient-to-l from-[#FF3D5A] to-[#FF6B9A] text-white brand-shadow-md">
        <div className="flex items-center gap-2 opacity-90"><WalletIcon size={20} /> الرصيد الحالي</div>
        <p className="text-4xl font-black mt-2">{bal.toFixed(2)} <span className="text-lg">د.ل</span></p>
        {balance.pending_balance > 0 && <p className="text-sm opacity-90 mt-1">قيد المعالجة: {Number(balance.pending_balance).toFixed(2)} د.ل</p>}
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4">
        <QuickBtn icon={Send} label="تحويل" onClick={() => navigate("/wallet/send")} testId="wallet-send" />
        <QuickBtn icon={ArrowDownToLine} label="سحب" onClick={() => navigate("/wallet/withdraw")} testId="wallet-withdraw" />
        <QuickBtn icon={History} label="السجل" onClick={() => navigate("/wallet/history")} testId="wallet-history" />
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-[#E8EAF2] p-5">
        <h2 className="font-black text-[#161629] mb-3 flex items-center gap-2"><Plus size={18} className="text-[#FF3D5A]" /> شحن المحفظة</h2>
        <input data-testid="topup-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="المبلغ بالدينار" className="w-full bg-[#F0F2FA] rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#FF3D5A]" />
        <div className="flex gap-2 mt-3">
          <button data-testid="topup-mode-tlync" onClick={() => setMode("tlync")} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-black text-sm ${mode === "tlync" ? "bg-[#FF3D5A] text-white" : "bg-[#F0F2FA] text-[#161629]"}`}><Zap size={16} /> T-Lync فوري</button>
          <button data-testid="topup-mode-manual" onClick={() => setMode("manual")} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-black text-sm ${mode === "manual" ? "bg-[#FF3D5A] text-white" : "bg-[#F0F2FA] text-[#161629]"}`}><CreditCard size={16} /> تحويل يدوي</button>
        </div>
        {mode === "manual" && (
          <div className="mt-3 space-y-2">
            {methods.map((m) => (
              <button key={m.method_id} onClick={() => setMethod(m)} className={`w-full flex items-center gap-2 p-3 rounded-xl border-2 text-start ${method?.method_id === m.method_id ? "border-[#FF3D5A] bg-[#FFE5EA]" : "border-[#E8EAF2]"}`}>
                <div className="flex-1"><p className="font-black text-sm">{m.name}</p>{m.details && <p className="text-xs text-[#6B7280]">{m.details}</p>}</div>
              </button>
            ))}
            <label className="flex flex-col items-center justify-center h-32 rounded-2xl border-2 border-dashed border-[#FF3D5A] bg-[#FFE5EA] cursor-pointer overflow-hidden">
              {receipt ? <img src={receipt} alt="" className="w-full h-full object-cover" /> : <><Upload size={26} className="text-[#FF3D5A]" /><span className="text-sm text-[#FF3D5A] font-bold mt-1">ارفع إيصال التحويل</span></>}
              <input type="file" accept="image/*" className="hidden" onChange={onReceipt} />
            </label>
          </div>
        )}
        <button data-testid="topup-submit" disabled={busy} onClick={topup} className="w-full h-12 rounded-xl bg-[#FF3D5A] text-white font-black mt-4 disabled:opacity-60">{busy ? "..." : "شحن الرصيد"}</button>
      </div>
    </div>
  );
}
function QuickBtn({ icon: Icon, label, onClick, testId }) {
  return <button data-testid={testId} onClick={onClick} className="flex flex-col items-center gap-1.5 py-4 rounded-2xl bg-white border border-[#E8EAF2] hover:brand-shadow-sm"><Icon size={22} className="text-[#FF3D5A]" /><span className="text-xs font-bold text-[#161629]">{label}</span></button>;
}
